import { Box, Button, Checkbox } from "@material-ui/core"
import { useEffect, useRef, useState } from "react"
import type { ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { DialogWindow, DialogWindowHeader } from ".."
import { config } from "../../../config"
import { useCallbackSafeState } from "../../../hooks"
import type { Bundle } from "../../../redux"
import { mapObjectDescriptorToTranslation } from "../../../utils"
import { Body1 } from "../../atoms"
import { ProjectEditDialog } from "../../organisms"

import "./unsaved-project-dialog.styles.scss"

export interface IUnsavedProjectDialogProps {
  isDirty: boolean
  hasId: boolean
  title: string
  bundles: Bundle[]
  entityName: "map" | "comparison"
  typeOfSave?: "goBackSave" | "saveButtonSave"
  onSave: (title: string, bundles: Bundle[]) => void
  onCancel?: () => void
}

export function UnsavedProjectDialog(props: IUnsavedProjectDialogProps): ReactElement {
  const { isDirty, hasId, title, bundles, entityName, typeOfSave = "goBackSave", onSave, onCancel } = props
  const { t } = useTranslation(["unsavedDialogComponent", "general"])

  const history = useHistory()

  const [showPrompt, setShowPrompt] = useState<boolean>(false)
  const [currentPath, setCurrentPath] = useState<string>("")
  const [back, setBack] = useState<boolean>(false)
  const [isDontShowChecked, setIsDontShowChecked] = useState<boolean>(false)
  const [getSafeIsDirty, setSafeIsDirty] = useCallbackSafeState<boolean>(isDirty)

  const unblockRef: any = useRef()

  useEffect(() => {
    addEventListeners()
    return () => {
      removeEventListeners()
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setSafeIsDirty(isDirty)

    unblockRef.current = history.block((location) => {
      if (isDirty) {
        setCurrentPath(location.pathname)
        setShowPrompt(true)
        return false
      }
    })
    return () => {
      unblockRef.current?.()
    }
  }, [isDirty])

  function addEventListeners(): void {
    window.addEventListener("beforeunload", onRefresh)
    window.addEventListener("popstate", onBackPressed)
  }

  function removeEventListeners(): void {
    window.removeEventListener("beforeunload", onRefresh)
    window.removeEventListener("popstate", onBackPressed)
  }

  function onRefresh(event: BeforeUnloadEvent): void {
    if (getSafeIsDirty()) {
      event.preventDefault()
      event.returnValue = false
    }
  }

  function onBackPressed(event: PopStateEvent): void {
    if (getSafeIsDirty()) {
      event.preventDefault()
      setBack(true)
      setShowPrompt(true)
    }
  }

  function setLocalStorageChoice(): void {
    window.localStorage.setItem(config.showUnsavedProjectChangesDialogLocalstorageKey, "false")
  }

  function handleDontSave(): void {
    history.block(() => {})
    setShowPrompt(false)
    back ? history.goBack() : history.push(currentPath)
  }

  function handleCancel(): void {
    setShowPrompt(false)
    if (onCancel) onCancel()
  }

  function handleProjectSave(): void {
    if (isDontShowChecked) setLocalStorageChoice()
    onSave(title, bundles)
    setShowPrompt(false)
    history.block(() => {})
    if (typeOfSave === "saveButtonSave") history.goBack()
    if (typeOfSave === "goBackSave") back ? history.goBack() : history.push(currentPath)
  }

  function UnsavedProjectWarningDialog(): ReactElement {
    return (
      <DialogWindow
        open={showPrompt || typeOfSave === "saveButtonSave"}
        onClose={handleCancel}
        className="unsaved-project-dialog"
      >
        <DialogWindowHeader
          title={t("unsavedDialogComponent:confirmUnsaving", {
            type: mapObjectDescriptorToTranslation(entityName)
          })}
          onClose={handleCancel}
        />
        <Box className="dialog-content">
          <Body1>
            {typeOfSave === "saveButtonSave"
              ? t("unsavedDialogComponent:warningShareWithStudents", {
                  type: mapObjectDescriptorToTranslation(entityName),
                  title
                })
              : t("unsavedDialogComponent:savingChangesNotLostWithTitle", {
                  type: mapObjectDescriptorToTranslation(entityName),
                  title
                })}
          </Body1>
          {typeOfSave === "goBackSave" && (
            <Box className="dialog-dont-show">
              <Checkbox
                id="dont-show-checkbox"
                color="primary"
                checked={isDontShowChecked}
                onChange={() => setIsDontShowChecked(!isDontShowChecked)}
              />
              <label htmlFor="dont-show-checkbox">
                <Body1>{t("general:dontShowMessage")}</Body1>
              </label>
            </Box>
          )}
        </Box>
        <Box className="footer">
          <Button color="primary" onClick={handleCancel}>
            {t("general:dialog.cancel")}
          </Button>
          {typeOfSave === "goBackSave" && (
            <Button color="primary" variant="outlined" onClick={handleDontSave}>
              {t("general:notSave")}
            </Button>
          )}

          <Button variant="contained" color="primary" onClick={handleProjectSave}>
            {t("general:save")}
          </Button>
        </Box>
      </DialogWindow>
    )
  }

  return hasId ? (
    <UnsavedProjectWarningDialog />
  ) : (
    <ProjectEditDialog
      dialogTitle={t("unsavedDialogComponent:confirmUnsaving", {
        type: mapObjectDescriptorToTranslation(entityName)
      })}
      message={t("unsavedDialogComponent:savingChangesNotLost", {
        type: mapObjectDescriptorToTranslation(entityName)
      })}
      open={showPrompt}
      entityName={entityName}
      bundles={[]}
      onDontSave={handleDontSave}
      onClose={handleCancel}
      onSave={onSave}
    />
  )
}
